import { money } from "@/plan-logic/lib/format";
import { ColumnHits, useColumnHover } from "./columns";
import { niceAxis, tickText } from "./scale";

export type StackSeries = { k: string; n: string; c: string };
export type StackRow = { label: string | number; values: Record<string, number> };

/**
 * The prototype's `stackChart` (v47): one stacked bar per row (year), a legend, an optional dashed
 * overlay line (the other plan) and a note. Gridlines land on readable numbers. Hovering lights the
 * column under the pointer (navy wash, nothing dims): on a segment the tip gives the segment, in
 * the gap the column's total (`colLabel`, "All accounts"). The tip is `aria-hidden`; the svg keeps
 * its label.
 */
export function StackChart({
  rows,
  series,
  height = 300,
  barWidth = 22,
  label,
  note,
  overlay,
  testId,
  colLabel = "All accounts",
}: {
  rows: StackRow[];
  series: StackSeries[];
  height?: number;
  barWidth?: number;
  label: string;
  note?: string;
  overlay?: { values: number[]; c: string; n: string };
  testId?: string;
  /** What a column's total is called in the tip ("All accounts", "Still owing"). */
  colLabel?: string;
}) {
  const W = 920;
  const H = height;
  const L = 66;
  const R = 16;
  const T = 16;
  const B = 46;
  const n = rows.length;
  const step = (W - L - R) / Math.max(1, n);
  const { wrap, tip, hover, onMove, onLeave } = useColumnHover({ n, L, step, W });
  if (!rows.length) return <p className="mut">Nothing to show for this part of the plan.</p>;
  const tot = (r: StackRow) => series.reduce((a, s) => a + Math.max(0, r.values[s.k] ?? 0), 0);
  const axis = niceAxis(0, Math.max(...rows.map(tot), ...(overlay?.values ?? [0])) * 1.06);
  const max = axis.hi;
  const bw = Math.min(barWidth, step * 0.72);
  const Y = (v: number) => H - B - (v / max) * (H - T - B);
  const every = Math.ceil(n / 8);
  const cx = (i: number) => L + step * i + step / 2;

  const hRow = hover ? rows[hover.i] : undefined;
  const hSeries = hover?.k ? series.find((s) => s.k === hover.k) : undefined;

  return (
    <div data-testid={testId} className="chwrap" ref={wrap}>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="bars"
        role="img"
        aria-label={label}
        onMouseMove={onMove}
        onMouseLeave={onLeave}
      >
        {axis.ticks.map((t) => (
          <g key={t}>
            <line x1={L} x2={W - R} y1={Y(t)} y2={Y(t)} stroke="var(--line2)" />
            <text x={L - 9} y={Y(t) + 4} textAnchor="end" className="bx">
              {tickText(t, axis.step)}
            </text>
          </g>
        ))}
        <ColumnHits n={n} L={L} step={step} T={T} bottom={H - B} on={hover?.i} />
        {rows.map((r, i) => {
          let acc = 0;
          return (
            <g key={`${r.label}-${i}`}>
              {series.map((s) => {
                const v = r.values[s.k] ?? 0;
                if (v <= 0) return null;
                const y0 = Y(acc + v);
                const y1 = Y(acc);
                acc += v;
                return (
                  <rect
                    key={s.k}
                    x={(cx(i) - bw / 2).toFixed(1)}
                    y={y0.toFixed(1)}
                    width={bw.toFixed(1)}
                    height={Math.max(0.8, y1 - y0 - 1.5).toFixed(1)}
                    rx={Math.min(3, bw / 4).toFixed(1)}
                    fill={s.c}
                    data-i={i}
                    data-k={s.k}
                    className={hover?.i === i && hover.k === s.k ? "hv" : undefined}
                  />
                );
              })}
            </g>
          );
        })}
        {overlay && (
          <path
            d={`M${rows.map((_, i) => `${cx(i).toFixed(1)} ${Y(overlay.values[i] ?? 0).toFixed(1)}`).join(" L")}`}
            fill="none"
            stroke={overlay.c}
            strokeWidth="2"
            strokeDasharray="5 5"
            pointerEvents="none"
          />
        )}
        {rows.map((r, i) =>
          i % every === 0 || i === n - 1 ? (
            <text key={`l${i}`} x={cx(i).toFixed(1)} y={H - 26} textAnchor="middle" className="bx">
              {r.label}
            </text>
          ) : null,
        )}
      </svg>
      <div
        ref={tip}
        className={`chtip${hRow ? " on" : ""}`}
        aria-hidden="true"
        style={hover ? { left: hover.left, top: hover.top } : undefined}
        data-testid={testId ? `${testId}-tip` : undefined}
      >
        {hRow && (
          <>
            <b>{hRow.label}</b>
            {hSeries ? hSeries.n : colLabel}{" "}
            <span className="v">{money(hSeries ? (hRow.values[hSeries.k] ?? 0) : tot(hRow))}</span>
          </>
        )}
      </div>
      <div className="legend" style={{ marginTop: 8 }}>
        {series.map((s) => (
          <span key={s.k}>
            <i className="lgd" style={{ background: s.c }} />
            {s.n}
          </span>
        ))}
        {overlay && (
          <span>
            <i className="lgd" style={{ background: overlay.c }} />
            {overlay.n}
          </span>
        )}
      </div>
      {note && <p className="hint">{note}</p>}
    </div>
  );
}
